import * as React from "react";
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "../../lib/utils";

export type AlertType = "success" | "error" | "info" | "warning";

export interface AlertProps {
  id: string;
  type: AlertType;
  message: string;
  link?: {
    text: string;
    href: string;
    onClick?: () => void;
  };
  duration?: number;
  onClose: (id: string) => void;
}

const alertIcons = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const alertStyles = {
  success: "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200",
  error: "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200",
  info: "bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30 text-primary dark:text-primary-soft",
  warning: "bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200",
};

const iconStyles = {
  success: "text-green-600 dark:text-green-400",
  error: "text-red-600 dark:text-red-400",
  info: "text-primary dark:text-primary-soft",
  warning: "text-yellow-600 dark:text-yellow-400",
};

export function Alert({ id, type, message, link, onClose }: AlertProps) {
  const Icon = alertIcons[type];

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 rounded-card border shadow-lg animate-in slide-in-from-top-5 fade-in-0 duration-300",
        alertStyles[type]
      )}
    >
      <Icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", iconStyles[type])} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {message}
          {link && (
            <>
              {" "}
              <a
                href={link.href}
                onClick={(e) => {
                  if (link.onClick) {
                    e.preventDefault();
                    link.onClick();
                  }
                }}
                className="underline hover:no-underline font-semibold"
              >
                {link.text}
              </a>
            </>
          )}
        </p>
      </div>
      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );
}
